import * as React from 'react';
import {weakMemo} from '@packages/utilities/memo';
import {TypeImplementation, Expressions} from '@packages/systems/wfdl';
import {
  EUnion,
  ECall,
  ERecord,
  EHole,
  TTypeReference,
} from '@packages/systems/wfdl-ast';
import {getCollectionNameFromVariable, selectCMSCollections} from '../utils';
import {CollectionSettings} from '../components/CollectionSettings';
import {useSharedFlowContext} from '@packages/systems/flows/shared';
import {
  ExpressionEditor,
  forwardDispatch,
  updateExpressionAtAction,
} from '@packages/systems/ExpressionEditor-utils';
import {confirm} from '@packages/systems/dialog';
import {a} from '@packages/utilities/wfdl-helpers';
import {TEffect} from '@packages/systems/plugins/Effect';
import {useDesignerSelector} from '@packages/systems/designer/flux';

import {
  ContextualExpressions,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';

const error = TTypeReference('error');
const itemDeletersType = TEffect(error, a);
const atArg = {in: 'Call', at: 'arg'} as const;
const dispatchAtArg = weakMemo(
  (dispatch: TypeImplementation.ExpressionEditorDispatch) =>
    forwardDispatch(dispatch)(atArg)
);
const ADD_ID_MANUALLY = 'Add item ID';

/**
 * Type that deletes a CMS item
 */
export const ItemDeleters = TypeImplementation.Alias({
  params: ['error', 'a'],
  system: {bindUIVariant: 'custom'},
  type: itemDeletersType,
  render: function ItemDeleters({expr, dispatch}) {
    const {useCMSBlock} = useSharedFlowContext();
    const {hasDependencies} = useCMSBlock();
    const collections = useDesignerSelector(selectCMSCollections);
    const call = ContextualExpressions.getCall(stripAnn(expr));
    const name = call
      ? getCollectionNameFromVariable(stripAll(call.fun))
      : null;
    const onCollectionChange = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'collectionName' implicitly has an 'any' type.
      async (collectionName) => {
        const coll = collections.find((c) => c.pluralName === collectionName);
        if (!coll) return;
        const newExpression = ECall({
          fun: Expressions.qualifiedVariable(
            '__SitePlugin',
            `Delete ${collectionName}`
          ),
          arg: ERecord({
            'Item ID': EUnion({
              tag: ADD_ID_MANUALLY,
              value: EHole(),
            }),
          }),
        });
        if (hasDependencies) {
          const response = await confirm({
            title: 'Change Collection?',
            content:
              "You're currently using data from the current collection in other parts of the flow. Changing a collection might break the flow.",
            submit: {label: 'Change Collection', intent: 'primary'},
            cancel: {label: 'Cancel', intent: 'default'},
          });
          if (response.type === 'confirm') {
            dispatch(updateExpressionAtAction(newExpression, []));
            return;
          } else {
            return;
          }
        }
        dispatch(updateExpressionAtAction(newExpression, []));
      },
      [dispatch, hasDependencies, collections]
    );

    return (
      <>
        <CollectionSettings
          onChange={onCollectionChange}
          value={name}
          collections={collections}
          blockType="Delete"
        />
        {call ? (
          <ExpressionEditor
            expression={call.arg}
            dispatch={dispatchAtArg(dispatch)}
          />
        ) : null}
      </>
    );
  },
});
